import { useState } from 'react'
import { Table, Button, Tag, Typography, Space, App, Modal, Input, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useAuth } from '../../../auth/AuthProvider'
import { isSysAdmin, isWorkspaceAdmin } from '../../../utils/roles'
import { approveTenantRequest, rejectTenantRequest, markTenantRequestCompleted } from '../services/tenant-request-api'
import type { TenantRequest, TenantRequestStatus } from '../types/tenant-request.types'
import { TenantRequestStatusLabels, TenantRequestStatusColors } from '../types/tenant-request.types'
import { restoreTemplateOptions } from '../../../constants/tenant.constants'
import './TenantRequestsTable.css'

const { TextArea } = Input
const { Text } = Typography

interface TenantRequestsTableProps {
  requests: TenantRequest[]
  loading: boolean
  onRefresh: () => void
  onEdit?: (request: TenantRequest) => void
  onCreateWorkspace?: (request: TenantRequest) => void
  canApprove: boolean
}

export function TenantRequestsTable({
  requests,
  loading,
  onRefresh,
  onEdit,
  onCreateWorkspace,
  canApprove
}: TenantRequestsTableProps) {
  const { message } = App.useApp()
  const { user } = useAuth()
  const [reviewModalOpen, setReviewModalOpen] = useState(false)
  const [reviewType, setReviewType] = useState<'approve' | 'reject'>('approve')
  const [selectedRequest, setSelectedRequest] = useState<TenantRequest | null>(null)
  const [reviewComments, setReviewComments] = useState('')
  const [reviewNotes, setReviewNotes] = useState('')

  // Get current user ID for ownership check
  const currentUserId = user?.profile?.sub
  const isSysAdminUser = isSysAdmin(user)
  const isWorkspaceAdminUser = isWorkspaceAdmin(user)

  const handleApprove = (request: TenantRequest) => {
    setSelectedRequest(request)
    setReviewType('approve')
    setReviewNotes(request.notes || '')
    setReviewModalOpen(true)
  }

  const handleReject = (request: TenantRequest) => {
    setSelectedRequest(request)
    setReviewType('reject')
    setReviewNotes(request.notes || '')
    setReviewModalOpen(true)
  }

  const handleReviewSubmit = async () => {
    if (!selectedRequest) return

    try {
      if (reviewType === 'approve') {
        await approveTenantRequest(selectedRequest.id, { comments: reviewComments, notes: reviewNotes || undefined })
        message.success('Request approved successfully')
      } else {
        await rejectTenantRequest(selectedRequest.id, { comments: reviewComments, notes: reviewNotes || undefined })
        message.success('Request rejected successfully')
      }
      setReviewModalOpen(false)
      setReviewComments('')
      setReviewNotes('')
      setSelectedRequest(null)
      onRefresh()
    } catch (error: any) {
      console.error('Failed to review request:', error)
      message.error(error.response?.data?.message || 'Failed to review request')
    }
  }

  const handleMarkCompleted = async (id: string) => {
    try {
      await markTenantRequestCompleted(id)
      message.success('Request marked as completed')
      onRefresh()
    } catch (error: any) {
      console.error('Failed to mark as completed:', error)
      message.error(error.response?.data?.message || 'Failed to mark as completed')
    }
  }

  const hasAnyDeactivation = requests.some(r => !!r.deactivationDate)

  const columns: ColumnsType<TenantRequest> = [
    {
      title: 'Workspace',
      dataIndex: 'workspaceName',
      key: 'workspaceName',
      width: 300,
      sorter: (a, b) => a.workspaceName.localeCompare(b.workspaceName),
      render: (workspaceName: string, record: TenantRequest) => (
        <Space size={8} wrap={false} style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
          <Text ellipsis style={{ maxWidth: '150px' }}>
            {workspaceName}
          </Text>
          <span>
            {record.tenantCode && (
              <Tooltip title="Tenant code">
                <Tag color="geekblue">{record.tenantCode.toLowerCase()}</Tag>
              </Tooltip>
            )}
            <Tooltip title="Workspace code">
              <Tag color="default">{record.workspaceCode.toLowerCase()}</Tag>
            </Tooltip>
          </span>
        </Space>
      )
    },
    // {
    //   title: 'Tenant code',
    //   dataIndex: 'tenantCode',
    //   key: 'tenantCode',
    //   render: (code: string) => <strong>{code.toUpperCase()}</strong>
    // },
    // {
    //   title: 'Workspace',
    //   dataIndex: 'workspaceName',
    //   key: 'workspaceName',
    //   render: (name: string, record) => (
    //     <div>
    //       <div>{name}</div>
    //       <div style={{ fontSize: '12px', color: '#888' }}>{record.workspaceCode}</div>
    //     </div>
    //   )
    // },
    {
      title: 'Type',
      dataIndex: 'workspaceType',
      key: 'workspaceType',
      width: hasAnyDeactivation ? 180 : 100,
      render: (type: string, record: TenantRequest) => {
        const shortLabels: Record<string, string> = { new: 'New', demo: 'Demo', trial: 'Trial', uat: 'UAT' }
        const colors: Record<string, string> = { new: 'blue', demo: 'orange', trial: 'purple', uat: 'magenta' }
        const label = shortLabels[type] || type || '-'
        const color = colors[type] || 'default'
        const deactivationDate = record.deactivationDate ? new Date(record.deactivationDate) : null
        return (
          <Space size={6} wrap={false} style={{ whiteSpace: 'nowrap' }}>
            <Tag color={color} style={{ marginRight: 0 }}>{label}</Tag>
            {deactivationDate && (
              <Tooltip title={`Deactivates on ${deactivationDate.toLocaleDateString()}`}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  exp {deactivationDate.toLocaleDateString()}
                </Text>
              </Tooltip>
            )}
          </Space>
        )
      }
    },
    {
      title: 'Owner email',
      dataIndex: 'ownerEmail',
      key: 'ownerEmail',
      sorter: (a, b) => a.ownerEmail.localeCompare(b.ownerEmail)
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 150,
      ellipsis: { showTitle: false },
      render: (notes: string | null) =>
        notes ? (
          <Tooltip title={notes} placement="topLeft">
            <span
              style={{
                display: 'block',
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {notes}
            </span>
          </Tooltip>
        ) : (
          '-'
        )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => {
        const statusOrder: Record<TenantRequestStatus, number> = { p: 1, a: 2, c: 3, r: 4 }
        return statusOrder[a.status] - statusOrder[b.status]
      },
      render: (status: TenantRequestStatus) => (
        <Tag color={TenantRequestStatusColors[status]}>{TenantRequestStatusLabels[status]}</Tag>
      )
    },
    {
      title: 'Requested by',
      dataIndex: 'requestedByName',
      key: 'requestedByName',
      sorter: (a, b) => (a.requestedByName || '').localeCompare(b.requestedByName || ''),
      render: (name: string | null, record: TenantRequest) => name || record.requestedBy.substring(0, 8)
    },
    {
      title: 'Requested date',
      dataIndex: 'requestedDate',
      key: 'requestedDate',
      sorter: (a, b) => new Date(a.requestedDate).getTime() - new Date(b.requestedDate).getTime(),
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: TenantRequest) => {
        const isOwnRequest = record.requestedBy === currentUserId
        const canEditRequest =
          (record.status === 'p' && (canApprove || isOwnRequest)) || (record.status === 'a' && isSysAdminUser)

        return (
          <Space>
            {canEditRequest && onEdit && (
              <Button size="small" onClick={() => onEdit(record)}>
                Edit
              </Button>
            )}
            {canApprove && record.status === 'p' && (
              <>
                <Button size="small" type="primary" ghost onClick={() => handleApprove(record)}>
                  Approve
                </Button>
                <Button size="small" danger onClick={() => handleReject(record)}>
                  Reject
                </Button>
              </>
            )}
            {record.status === 'a' && (isSysAdminUser || isWorkspaceAdminUser) && (
              <>
                {onCreateWorkspace && (
                  <Button size="small" type="primary" onClick={() => onCreateWorkspace(record)}>
                    Create workspace
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={() => handleMarkCompleted(record.id)}
                  style={{ color: '#42a10a', borderColor: '#42a10a' }}
                >
                  Mark completed
                </Button>
              </>
            )}
          </Space>
        )
      }
    }
  ]

  return (
    <>
      <div
        className="tenant-requests-table-wrapper"
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Table
          size="small"
          columns={columns}
          dataSource={requests}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content', y: 'calc(100vh - 245px)' }}
          pagination={false}
          style={{ flex: 1 }}
          expandable={{
            expandedRowRender: record => (
              <div style={{ padding: '12px', background: '#fafafa', borderRadius: '4px' }}>
                <p>
                  <strong>Partner code:</strong> {record.partnerCode || 'N/A'}
                </p>
                <p>
                  <strong>Restore from template:</strong>{' '}
                  {record.restoreFromTemplate
                    ? restoreTemplateOptions.find(opt => opt.value === record.restoreFromTemplate)?.label ||
                      record.restoreFromTemplate
                    : 'None'}
                </p>
                {record.copyFromWorkspaceCode && (
                  <p>
                    <strong>Copy from workspace:</strong> {record.copyFromWorkspaceCode}
                  </p>
                )}
                {record.deactivationDate && (
                  <p>
                    <strong>Deactivation date:</strong> {new Date(record.deactivationDate).toLocaleDateString()}
                  </p>
                )}
                {record.deactivationReason && (
                  <p>
                    <strong>Deactivation reason:</strong> {record.deactivationReason}
                  </p>
                )}
                <p>
                  <strong>Feature flags:</strong> {record.featureFlags.join(', ') || 'None'}
                </p>
                {record.reviewedBy && (
                  <>
                    <p>
                      <strong>Reviewed by:</strong> {record.reviewedBy}
                    </p>
                    <p>
                      <strong>Review date:</strong>{' '}
                      {record.reviewedDate ? new Date(record.reviewedDate).toLocaleString() : 'N/A'}
                    </p>
                    {record.reviewComments && (
                      <p>
                        <strong>Comments:</strong> {record.reviewComments}
                      </p>
                    )}
                  </>
                )}
                {record.completedDate && (
                  <p>
                    <strong>Completed date:</strong> {new Date(record.completedDate).toLocaleString()}
                  </p>
                )}
              </div>
            )
          }}
        />
      </div>

      <Modal
        title={reviewType === 'approve' ? 'Approve request' : 'Reject request'}
        open={reviewModalOpen}
        onOk={handleReviewSubmit}
        onCancel={() => {
          setReviewModalOpen(false)
          setReviewComments('')
          setReviewNotes('')
          setSelectedRequest(null)
        }}
        okText={reviewType === 'approve' ? 'Approve' : 'Reject'}
        okButtonProps={{ danger: reviewType === 'reject' }}
      >
        <p>
          Are you sure you want to {reviewType} the workspace request for{' '}
          <strong>{selectedRequest?.workspaceName}</strong>?
        </p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Notes</label>
          <TextArea
            rows={2}
            placeholder="Add notes (optional)"
            value={reviewNotes}
            onChange={e => setReviewNotes(e.target.value)}
            maxLength={1000}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Review comments</label>
          <TextArea
            rows={3}
            placeholder="Add review comments (optional)"
            value={reviewComments}
            onChange={e => setReviewComments(e.target.value)}
          />
        </div>
      </Modal>
    </>
  )
}
